/**
 * @fileoverview Many photos onto the tray, one at a time underneath: the sending the Photos page's
 * "Choose photos" (TrayUploader) and the add pop-ups' "Take or upload a photo" (usePhotoAdd, D78) share.
 *
 * Each picture is shrunk here, the way a single upload is (preparePhoto: an iPhone HEIC made a JPEG, its date
 * read from the original), and sent on its own: a dropped connection loses one picture rather than the batch, and
 * the count can say how far it has got. A picture that cannot be made ready is not sent, since the server could not
 * read it, and counts among the ones that failed. Each photo that lands is put on the tray at once
 * (trayPhotoLanded), so it shows in the pile the server puts it in as it lands, and is handed to onLanded (the add
 * pop-ups keep the batch's photos, to show those first).
 *
 * Moved out of TrayUploader for D78 (2026-10-22), unchanged in what it does.
 *
 * An exact copy of a photo already added (the server answers duplicate_of, B61) is not stored and not counted as
 * failed: the batch ends with "Already added, N copies skipped" and Undo (lib/photoCopies), and a copy Undo adds is
 * handed to onLanded like any other. A Google Photos pick sends Google's item id with the photo.
 *
 * A photo that gets no answer (a timeout, a dropped connection, or 502/503/504 while the server restarts) is sent once
 * more after TRAY_UPLOAD_RETRY_WAIT_MS (B82, lib/trayRetry): the live server once stopped mid-upload and the photo was
 * lost. The first failure is still reported to Sentry. A copy answer to that retry means the first one landed after
 * all: it counts as landed (not "Already added"), and the photo, found on the tray once it is read again, is handed to
 * onLanded. A refusal or a 500 is not retried.
 *
 * Where: TrayUploader (the Photos page), usePhotoAdd (Plants, Beds, Seasons and Today).
 * API: POST /tray (addToTray, multipart: the photo, taken_at, locationId), TRAY_UPLOAD_CONCURRENCY at a time, each
 * sent once more on no answer; GET /tray again once the batch is in.
 * Settings: TRAY_UPLOAD_CONCURRENCY, TRAY_UPLOAD_RETRY_WAIT_MS (config/tunables/garden).
 * Words (en/es): photoCopies.* (sayCopiesSkipped); otherwise none of its own: a failure's words are the caller's to say (Progress: ours, the standard sentence for a
 * failure on our side, reported to Sentry; refused, the refusal's own words; one or the other per failure, lib/batchFailure, B104).
 *
 * @module useTrayBatch
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import { isHeic } from '@/shared/lib/uploadablePhoto';
import { addToTray, type Tray, type TrayPhoto } from '@/shared/api/tray';
import { DateTime } from '@/shared/utils/dateUtils';
import { debugLog } from '@/config/config';
import { reportServerFailure } from '@/shared/lib/serverFailure';
import type { ChosenPicture } from '@/features/plant-list/components/dialogs/photo/PhotoSourcePicker';
import { PhotoCopySkipped } from '@/shared/lib/photoCopiesCore';
import { sayCopiesSkipped } from '@/shared/lib/photoCopies';
import { TRAY_QUERY_KEY, trayPhotoLanded } from './useTray';
import { sendWithOneRetry } from './trayRetry';
import { batchFailureWords } from './batchFailure';
import { TRAY_UPLOAD_CONCURRENCY as UPLOAD_CONCURRENCY, TRAY_UPLOAD_RETRY_WAIT_MS } from '@/config/tunables/garden';

/** How far a batch has got, and how many of it failed. */
export interface BatchProgress {
  /** Sent or failed so far. */
  done: number;
  /** In the batch. */
  total: number;
  /** Not sent (an upload refused, or an iPhone photo that would not convert). */
  failed: number;
  /** The standard sentence, once an upload has failed on our side; null otherwise. */
  ours: string | null;
  /** The first refusal's words (a photo too big in pixels, an iPhone photo that would not convert), once one was; null otherwise. */
  refused: string | null;
}

/** What the sending is told. */
export interface TrayBatchOptions {
  /** Each photo as it lands on the tray. */
  onLanded?: (photo: TrayPhoto) => void;
  /** True while a batch is going in, false once it is in. */
  onUploadingChange?: (uploading: boolean) => void;
}

/** What the hook hands back. */
export interface TrayBatch {
  /** Sends the pictures chosen, a few at a time, counting as it goes. */
  send: (chosen: ChosenPicture[]) => Promise<void>;
  /** The batch's progress: null before one, and again once one went in with no failure. */
  progress: BatchProgress | null;
  /** True while a batch is going in. */
  busy: boolean;
}

/** A picture that must not be sent (preparePhoto refused it), carrying the sentence for the gardener. */
class PhotoRefused extends Error {}

/**
 * Waits a while (the pause before a tray upload's one retry).
 *
 * @param ms - How long, in milliseconds.
 * @returns Resolves once it has passed.
 */
const pause = (ms: number): Promise<void> => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Sends batches of photos to the tray from one bed (or none).
 *
 * @param locationId - The bed the photos are from, sent as the tray's hint; null for none (the card asks).
 * @param options - onLanded and onUploadingChange.
 * @returns send, progress and busy.
 */
export function useTrayBatch(locationId: number | null, options: TrayBatchOptions = {}): TrayBatch {
  const queryClient = useQueryClient();
  const [progress, setProgress] = React.useState<BatchProgress | null>(null);
  // The latest callbacks, so a batch under way tells whoever is listening now.
  const told = React.useRef(options);
  told.current = options;

  /**
   * Shrinks one picture (converting an iPhone HEIC first) and puts it on the tray, sending it once more when the
   * first send gets no answer (lib/trayRetry).
   *
   * @param picture - The file, and the day it was taken where its source says (Google's copies carry no EXIF; a
   *   camera frame was taken now).
   * @returns The photo as the tray has it, or, when the retry was answered as a copy of the first send that had
   *   landed, the tray id the server named (null when it did not name one on the tray).
   * @throws When the picture is a HEIC that could not be converted, or the upload fails.
   */
  const sendOne = async ({ file, takenAt: said, googleItemId }: ChosenPicture): Promise<TrayPhoto | { landedId: number | null }> => {
    let takenAt: DateTime | null = said ? DateTime.fromJSDate(said) : null;
    const prepared = await preparePhoto(file);
    // A picture that could not be made ready is not sent (the one rule, preparePhoto): counted as failed, and said.
    if (!prepared.ok) throw new PhotoRefused(prepared.message);
    takenAt = takenAt ?? prepared.exif.takenAt ?? null;
    // A file with no EXIF still knows when it was last written, which for a photo straight off the camera is when it was taken.
    if (!takenAt && file.lastModified) takenAt = DateTime.fromMillis(file.lastModified);
    const sent = await sendWithOneRetry(() => addToTray(prepared.file, takenAt, locationId, { googleItemId }), {
      waitMs: TRAY_UPLOAD_RETRY_WAIT_MS,
      wait: pause,
      isCopy: (error) => error instanceof PhotoCopySkipped,
      // Still reported, though the gardener sees nothing unless the retry fails too.
      onFirstFailure: (error) => reportServerFailure(error, 'Tray upload (sent again)'),
    });
    if ('landedAs' in sent) {
      const place = (sent.landedAs as PhotoCopySkipped).duplicateOf;
      return { landedId: place?.source === 'tray' ? place.id : null };
    }
    const photo = sent.photo;
    // Shown as it lands, not when the batch ends.
    trayPhotoLanded(queryClient, photo);
    told.current.onLanded?.(photo);
    return photo;
  };

  /**
   * Sends a batch, a few at a time (UPLOAD_CONCURRENCY), counting as it goes.
   *
   * @param chosen - The pictures chosen, from whichever source.
   */
  const send = async (chosen: ChosenPicture[]): Promise<void> => {
    // Pictures only. An iPhone photo picked on a computer often comes with no type at all, so a HEIC is recognised by its name or its bytes instead.
    const isPicture = await Promise.all(chosen.map(({ file }) => (file.type.startsWith('image/') ? true : isHeic(file))));
    const pictures = chosen.filter((_, index) => isPicture[index]);
    if (pictures.length === 0) return;
    setProgress({ done: 0, total: pictures.length, failed: 0, ours: null, refused: null });
    told.current.onUploadingChange?.(true);

    const queue = [...pictures];
    /** The exact copies the server skipped in this batch (B61). */
    const skipped: PhotoCopySkipped<TrayPhoto>[] = [];
    /** Photos whose retry was answered as a copy of the first send, which had landed (B82): their tray ids. */
    const landedOnRetry: number[] = [];
    /** Takes pictures off the shared queue one after another until it is empty. */
    const worker = async () => {
      for (let picture = queue.shift(); picture; picture = queue.shift()) {
        try {
          const sent = await sendOne(picture);
          if ('landedId' in sent && sent.landedId !== null) landedOnRetry.push(sent.landedId);
          setProgress((p) => p && { ...p, done: p.done + 1 });
        } catch (error) {
          // An exact copy of a photo already added: not stored, not a failure (B61).
          if (error instanceof PhotoCopySkipped) {
            skipped.push(error as PhotoCopySkipped<TrayPhoto>);
            setProgress((p) => p && { ...p, done: p.done + 1 });
            continue;
          }
          debugLog('TrayUploader', 'Upload failed', error);
          // On our side: reported, and said under the count; a refusal (a 413 for pixels, or the device's own check)
          // says why in its own words. One or the other, never both (B104: the request limit's sentence showed twice).
          const { ours, refused } = batchFailureWords(error, error instanceof PhotoRefused ? error.message : null);
          setProgress((p) => p && { ...p, done: p.done + 1, failed: p.failed + 1, ours: p.ours ?? ours, refused: p.refused ?? refused });
        }
      }
    };
    await Promise.all(Array.from({ length: UPLOAD_CONCURRENCY }, worker));

    await queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
    // A photo that landed on the first send though no answer came back: handed on, now the tray has been read again.
    if (landedOnRetry.length > 0) {
      const onTray = queryClient.getQueriesData<Tray>({ queryKey: TRAY_QUERY_KEY }).flatMap(([, tray]) => tray?.photos ?? []);
      landedOnRetry.forEach((trayId) => {
        const photo = onTray.find((each) => each.tray_id === trayId);
        if (photo) told.current.onLanded?.(photo);
      });
    }
    // "Already added, 1 copy skipped", with Undo, which adds them after all: then they are this batch's too.
    sayCopiesSkipped(skipped, (stored) => {
      stored.forEach((photo) => { trayPhotoLanded(queryClient, photo); told.current.onLanded?.(photo); });
      void queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
    });
    setProgress((p) => (p && p.failed > 0 ? p : null));
    told.current.onUploadingChange?.(false);
  };

  const busy = progress !== null && progress.done < progress.total;
  return { send, progress, busy };
}
